import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Baby, Loader2 } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import type { Hospital, VaginalDelivery } from '../types/clinical';

interface LogbookDelivery extends VaginalDelivery {
  patientName: string;
}

interface DeliveryGroup {
  name: string;
  count: number;
}

/**
 * Delivery Logbook — parallel to the Surgical Logbook but backed by the
 * /clinical/delivery-logbook collection-group aggregation. Filters by hospital,
 * delivery mode (NVD, Vacuum, Forceps, Breech, Other), and date range.
 */
export function DeliveryLogbookPage() {
  const navigate = useNavigate();
  const { get } = useApi();
  const { userRole } = useAuth();

  const [deliveries, setDeliveries] = useState<LogbookDelivery[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterHospital, setFilterHospital] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  // Detail view
  const [selectedMode, setSelectedMode] = useState<string | null>(null);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterHospital) params.set('hospital', filterHospital);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      const res = await get<LogbookDelivery[]>(`/clinical/delivery-logbook?${params}`);
      if (res.success && res.data) setDeliveries(res.data);
      else setError(res.error || 'Failed to load delivery logbook');
    } catch {
      setError('Failed to load delivery logbook');
    } finally {
      setLoading(false);
    }
  }, [get, filterHospital, filterStartDate, filterEndDate]);

  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  useEffect(() => {
    (async () => {
      const res = await get<Hospital[]>('/config/hospitals');
      if (res.success && res.data) setHospitals(res.data.filter(h => h.active));
    })();
  }, [get]);

  // Client-side filters
  const filtered = useMemo(() => {
    let result = deliveries;
    if (filterMode) result = result.filter(d => d.deliveryMode === filterMode);
    return result;
  }, [deliveries, filterMode]);

  // Grouped by delivery mode
  const modeGroups = useMemo((): DeliveryGroup[] => {
    const counts: Record<string, number> = {};
    for (const d of filtered) {
      const key = d.deliveryMode || 'Unspecified';
      counts[key] = (counts[key] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  const detailRows = useMemo(() => {
    if (!selectedMode) return [];
    return filtered.filter(d => (d.deliveryMode || 'Unspecified') === selectedMode);
  }, [filtered, selectedMode]);

  const resetFilters = () => {
    setFilterHospital('');
    setFilterMode('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  if (userRole !== 'doctor') {
    return <main className="max-w-7xl mx-auto px-4 py-8"><p className="text-gray-500">Doctor access only.</p></main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Baby className="h-6 w-6 text-teal-600" />
        <h1 className="text-2xl font-bold text-gray-900">Delivery Logbook</h1>
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Filter sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Filters</h3>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Hospital</label>
              <select value={filterHospital} onChange={e => setFilterHospital(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">All</option>
                {hospitals.map(h => <option key={h.id} value={h.name}>{h.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Delivery Mode</label>
              <select
                value={filterMode}
                onChange={e => setFilterMode(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
              >
                <option value="">All</option>
                <option value="NVD">Normal Vaginal Delivery (NVD)</option>
                <option value="Vacuum">Vacuum (Ventouse)</option>
                <option value="Forceps">Forceps</option>
                <option value="Breech">Vaginal Breech Delivery</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
                <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <button onClick={resetFilters} className="text-sm text-blue-600 hover:text-blue-800">Reset Filters</button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="md:col-span-2">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
          ) : !selectedMode ? (
            <div>
              <div className="bg-teal-600 text-white px-4 py-2 rounded-t-lg font-semibold flex justify-between items-center">
                <span>Deliveries</span>
                <span className="bg-white text-teal-700 rounded-full px-2.5 py-0.5 text-sm font-semibold">{filtered.length}</span>
              </div>
              {modeGroups.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-b-lg p-8 text-center text-gray-500">No deliveries found.</div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-b-lg divide-y divide-gray-100">
                  {modeGroups.map(g => (
                    <button
                      key={g.name}
                      onClick={() => setSelectedMode(g.name)}
                      className="w-full px-4 py-3 flex justify-between items-center hover:bg-gray-50 cursor-pointer text-left"
                    >
                      <span className="text-sm font-medium text-gray-900">{g.name}</span>
                      <span className="bg-teal-600 text-white rounded-full px-2.5 py-0.5 text-sm font-semibold">{g.count}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <button onClick={() => setSelectedMode(null)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mb-3">
                <ArrowLeft className="h-4 w-4" /> Back to summary
              </button>
              <div className="bg-teal-600 text-white px-4 py-2 rounded-t-lg font-semibold flex justify-between items-center">
                <span>{selectedMode}</span>
                <span className="bg-white text-teal-700 rounded-full px-2.5 py-0.5 text-sm font-semibold">{detailRows.length}</span>
              </div>
              <div className="bg-white border border-gray-200 rounded-b-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hospital</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Blood Group</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Delivery</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cord pH</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {detailRows.map(d => {
                      const rh = d.rhFactor === 'positive' ? '+' : d.rhFactor === 'negative' ? '−' : '';
                      const blood = d.bloodGroup ? `${d.bloodGroup}${rh}` : '—';
                      const modeLabels: Record<string, string> = {
                        NVD: 'NVD', Vacuum: 'Vacuum', Forceps: 'Forceps',
                        Breech: 'Breech', NVS: 'NVS', Other: 'Other',
                      };
                      return (
                        <tr key={d.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">{d.date}</td>
                          <td className="px-4 py-3 text-sm">
                            <button onClick={() => navigate(`/clinical/${d.patientId}`)} className="text-blue-600 hover:underline">
                              {d.patientName || 'Unknown'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{d.hospital || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{blood}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{modeLabels[d.deliveryMode] || d.deliveryMode || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{d.umbilicalCordPh || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
